import React from "react";
import {NodeSelection} from "@tiptap/pm/state";
import {ToggleButton, ToggleButtonGroup} from "@mui/material";
import {useTranslation} from "react-i18next";
import CNumberField from "crqlar-react-common/dist/components/CNumberField";
import {createResizedPrintoutContainerNode} from "../../../blocks/container/PrintoutContainerNodes";
import {
    clampPrintoutContainerColumns,
    MAX_PRINTOUT_CONTAINER_COLUMNS,
    MIN_PRINTOUT_CONTAINER_COLUMNS,
    normalizePrintoutContainerSpaceAbove,
    PRINTOUT_CONTAINER_SPACE_ABOVE_OPTIONS,
} from "../../../blocks/container/printoutContainerOptions";
import {PanelField, PanelHint} from "../PrintoutPanelSection";
import type {PrintoutBlockSettingsProps} from "./printoutBlockAttributes";
import {updateSelectedPrintoutBlockAttributes} from "./printoutBlockAttributes";

// Columns are child nodes, so resizing rebuilds the container in one transaction.
export function ContainerSettings({block, disabled, editor}: PrintoutBlockSettingsProps) {
    const {t} = useTranslation();

    const columnCount = block.node.childCount;

    const setColumnCount = (nextCount: number) => {
        const safeCount = clampPrintoutContainerColumns(nextCount);

        if (safeCount === columnCount || editor.isDestroyed) return;

        const {selection} = editor.state;
        const containerWasSelected = selection instanceof NodeSelection && selection.from === block.pos;
        const transaction = editor.state.tr.replaceWith(
            block.pos,
            block.pos + block.node.nodeSize,
            createResizedPrintoutContainerNode(block.node, safeCount),
        );

        // Preserve a NodeSelection so the settings panel stays open.
        if (containerWasSelected) {
            transaction.setSelection(NodeSelection.create(transaction.doc, block.pos));
        }

        editor.view.dispatch(transaction);
    };

    return (
        <>
            <PanelField label={t('Columns')}>
                <CNumberField
                    disabled={disabled}
                    fullWidth
                    max={MAX_PRINTOUT_CONTAINER_COLUMNS}
                    min={MIN_PRINTOUT_CONTAINER_COLUMNS}
                    onChange={(value) => {
                        if (value !== null) setColumnCount(value);
                    }}
                    size={'small'}
                    step={1}
                    value={columnCount}
                />
            </PanelField>

            <PanelHint>
                {t('Removing columns moves their content into the last remaining column.')}
            </PanelHint>

            {columnCount > 1 && (
                <PanelField
                    label={t('Column separators')}
                    hint={t('Print a thin line halfway between columns. The line does not add padding or change their width.')}
                >
                    <ToggleButtonGroup
                        aria-label={t('Column separators')}
                        disabled={disabled}
                        exclusive
                        fullWidth
                        onChange={(_, value: boolean | null) => {
                            if (value !== null) updateSelectedPrintoutBlockAttributes(editor, block, {columnSeparators: value});
                        }}
                        size="small"
                        value={block.node.attrs.columnSeparators === true}
                    >
                        <ToggleButton sx={{textTransform: 'none'}} value={false}>{t('None')}</ToggleButton>
                        <ToggleButton sx={{textTransform: 'none'}} value={true}>{t('Line')}</ToggleButton>
                    </ToggleButtonGroup>
                </PanelField>
            )}

            <PanelField
                label={t('Space above')}
                hint={t('Space before this container on the printed page. Use it to separate sections without empty paragraphs.')}
            >
                <ToggleButtonGroup
                    aria-label={t('Space above')}
                    disabled={disabled}
                    exclusive
                    fullWidth
                    onChange={(_, value: number | null) => {
                        if (value !== null) updateSelectedPrintoutBlockAttributes(editor, block, {spaceAboveMm: value});
                    }}
                    size="small"
                    value={normalizePrintoutContainerSpaceAbove(block.node.attrs.spaceAboveMm)}
                >
                    {PRINTOUT_CONTAINER_SPACE_ABOVE_OPTIONS.map(space => (
                        <ToggleButton key={space} sx={{textTransform: 'none'}} value={space}>
                            {space ? `${space} mm` : t('None')}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </PanelField>
        </>
    );
}

export default ContainerSettings;
